import { cva } from 'class-variance-authority';
import clsx from 'clsx';
import { Flex, Progress, Text } from '@nula/core-ui';

import { creditState } from './credit-state';
import { formatCreditResetDate } from './format';
import styles from './credit-meter.module.css';

const root = cva(styles.root);

export type CreditMeterProps = {
  balance: number;
  monthlyAllowance: number;
  /** RFC3339 instant the allowance refills. */
  creditsResetAt: string;
  className?: string;
};

type MeterState = 'normal' | 'warning' | 'danger';

function meterState(balance: number): MeterState {
  const state = creditState(balance);
  if (state === 'overdrawn' || state === 'blocked') return 'danger';
  if (state === 'low') return 'warning';
  return 'normal';
}

const strokeColors: Record<MeterState, string> = {
  normal: 'var(--primary)',
  warning: 'var(--warning)',
  danger: 'var(--error)',
};

export function CreditMeter({
  balance,
  monthlyAllowance,
  creditsResetAt,
  className,
}: CreditMeterProps) {
  const state = meterState(balance);
  // A spent balance reads full-but-muted: an empty bar and an overflowing bar
  // are visually identical, and full reads as "used up".
  const percent =
    balance < 0
      ? 100
      : monthlyAllowance > 0
        ? Math.min(100, Math.max(0, (balance / monthlyAllowance) * 100))
        : 0;

  return (
    <Flex vertical gap="xs" className={clsx(root(), className)}>
      <Flex align="baseline" gap="xs">
        <Text className={styles.balance}>{balance.toLocaleString()}</Text>
        {monthlyAllowance > 0 && (
          <Text size="sm" type="secondary" className={styles.allowance}>
            / {monthlyAllowance.toLocaleString()}
          </Text>
        )}
      </Flex>

      <Flex data-state={state} data-testid="credit-meter-track" className={styles.track}>
        <Progress
          className={styles.progress}
          percent={percent}
          showInfo={false}
          size="sm"
          status="normal"
          strokeColor={strokeColors[state]}
          role="progressbar"
          aria-label="Credits remaining this period"
          aria-valuenow={Math.round(percent)}
          aria-valuemin={0}
          aria-valuemax={100}
        />
      </Flex>

      <Text size="xs" type="secondary">
        Resets {formatCreditResetDate(creditsResetAt)}
      </Text>
    </Flex>
  );
}
